(ns io.daskul.webapp.widgets.form.selector
  "Unified selector widget supporting multiple UI variants.

   Supports:
   - :select - Standard dropdown (default)
   - :radio - Radio buttons with horizontal/vertical orientation
   - :radio-card - Card-style radio buttons with description
   - :checkbox - Checkbox group with horizontal/vertical orientation
   - :segmented - Segmented control (toggle style)
   - :autocomplete - Server-side search autocomplete
   - :multi-select - Multi-select dropdown"
  (:require
   [uix.core :as uix :refer [defui $]]
   [io.daskul.webapp.ui :as ui]
   [io.daskul.webapp.widgets.form.selector-logic :as selector-logic]))

(defn- extras-under-input
  "A Mantine `inputContainer` that stacks a selector's extra controls — the
   \"+ Add New...\" button, the \"Please specify...\" input — INSIDE the field's
   own input wrapper, under the input.

   They used to be returned BESIDE the input in a fragment. A fragment renders
   no DOM node of its own, so inside a multi-column form row (F-056) each extra
   became a grid item in its own right and auto-placed into the NEXT column,
   under the neighbouring field's label and reading as though it belonged to it
   (#776). One element is one grid item, which is what a field is.

   Inside the input wrapper rather than around the whole widget, because
   form-row.css aligns a row through each field's `.mantine-InputWrapper-root`
   being a DIRECT child of the row (#770). An outer div would break that chain
   and the field would quietly stop aligning with its neighbours — still
   correct on its own, just no longer level with them.

   A stretching column, which is what the extras got as grid items; their own
   `mt` is the gap. The class is form-row.css's hook: this div sits between the
   wrapper root and the input, so the offset reset there has to reach through
   it."
  [extras]
  (fn [input]
    ($ :div {:className "fw-input-extras"
             :style {:display "flex" :flexDirection "column"}}
      input
      extras)))

(defui selector-field
  "Unified selector widget.

   Props:
   - field-key: keyword - field identifier
   - value: string | vector - current value(s)
   - on-change: fn - called with new value
   - options: vector - [{:value \"...\" :label \"...\" :description \"...\"}]
   - loading?: boolean - show loading state
   - disabled?: boolean - disable input
   - error: string | nil - error message
   - ui: map - field UI config
   - waiting-for-dependent?: boolean - waiting for dependent field
   - dependent-field-name: keyword - name of dependent field
   - on-search: fn - for autocomplete search
   - search-query: string - current search query"
  [{:keys [field-key value on-change options loading? disabled? error required ui
           waiting-for-dependent? dependent-field-name
           on-search search-query on-create-new create-label]}]
  (let [label (:label ui)
        widget (keyword (or (:widget ui) "select"))
        orientation (keyword (or (:orientation ui) "vertical"))
        horizontal? (= orientation :horizontal)
        min-chars (or (:min-chars ui) 2)

        multi-select? (selector-logic/multi? ui)

        ;; "Show text input on" support (e.g., show free-text when "other" is selected)
        show-text-on (:show-text-input-on ui)
        option-values (when show-text-on (set (map :value options)))
        ;; Custom value = not nil, not empty, and not a predefined option
        has-custom-value? (and show-text-on
                               (some? value)
                               (not= value "")
                               (not (contains? option-values value)))
        [other-text set-other-text!] (uix/use-state (if has-custom-value? value ""))
        ;; Sync other-text when value loads asynchronously (e.g., edit page)
        _ (uix/use-effect
           (fn []
             (when has-custom-value?
               (set-other-text! value))
             js/undefined)
           [has-custom-value? value])

        ;; Autocomplete: internal state (hooks must be unconditional)
        [ac-text set-ac-text!] (uix/use-state "")
        ac-just-selected (uix/use-ref false)
        ac-input-ref (uix/use-ref nil)
        ;; Display text for autocomplete: show matched label when value is set
        ;; (edit page load, post-create). Falls back to user's typed text.
        ;; Computed synchronously — no effect timing gap.
        ac-display (if (and (= widget :autocomplete) value (seq options))
                     (or (:label (first (filter #(= (:value %) value) options)))
                         ac-text)
                     ac-text)

        placeholder (cond
                      waiting-for-dependent? (str "Select " (name (or dependent-field-name :dependent)) " first")
                      loading? "Loading..."
                      (= widget :autocomplete) (str "Type " min-chars "+ chars to search...")
                      :else (or (:placeholder ui) "Select..."))

        ;; For show-text-on: treat custom values as valid (select shows trigger value)
        value-valid? (or (= widget :autocomplete)
                         (nil? value)
                         (empty? value)
                         has-custom-value?
                         (and (seq options)
                              (if multi-select?
                                (every? (fn [v] (some #(= (:value %) v) options)) value)
                                (some #(= (:value %) value) options))))
        effective-value (cond
                          has-custom-value? show-text-on
                          value-valid? value
                          :else nil)]

    (case (selector-logic/render-as ui)
      :radio
      (let [layout-component (if horizontal? ui/group ui/stack)]
        ($ ui/radio-group
          {:label label
           :value effective-value
           :onChange on-change
           :required required
           :error error}
          ($ layout-component {:gap "xs"}
            (for [{opt-value :value opt-label :label opt-desc :description} options]
              ($ ui/radio
                {:key opt-value
                 :value opt-value
                 :label (if opt-desc
                          ($ ui/stack {:gap 0}
                            ($ ui/text {:size "sm"} opt-label)
                            ($ ui/text {:size "xs" :c "dimmed"} opt-desc))
                          opt-label)
                 :disabled (or disabled? loading? waiting-for-dependent?)})))))

      :radio-card
      (let [layout-component (if horizontal? ui/group ui/stack)]
        ($ ui/stack {:gap 4}
          ($ ui/text {:size "sm" :fw 500} label)
          ($ ui/radio-group
            {:value effective-value
             :onChange on-change}
            ($ layout-component {:gap "sm"}
              (for [{opt-value :value opt-label :label opt-desc :description} options]
                ($ ui/radio-card
                  {:key opt-value
                   :value opt-value
                   :disabled (or disabled? loading? waiting-for-dependent?)
                   :style {:padding "12px"}}
                  ($ ui/group {:wrap "nowrap" :align "flex-start"}
                    ($ ui/radio-indicator)
                    ($ ui/stack {:gap 0}
                      ($ ui/text {:size "sm" :fw 500} opt-label)
                      (when opt-desc
                        ($ ui/text {:size "xs" :c "dimmed"} opt-desc))))))))
          (when error
            ($ ui/text {:size "xs" :c "red"} error))))

      :checkbox
      (let [layout-component (if horizontal? ui/group ui/stack)]
        ($ ui/checkbox-group
          {:label label
           :value (clj->js (or effective-value []))
           :onChange #(on-change (js->clj %))
           :required required
           :error error}
          ($ layout-component {:gap "xs"}
            (for [{opt-value :value opt-label :label} options]
              ($ ui/checkbox
                {:key opt-value
                 :value opt-value
                 :label opt-label
                 :disabled (or disabled? loading? waiting-for-dependent?)})))))

      :segmented
      ($ ui/stack {:gap 4}
        ($ ui/text {:size "sm" :fw 500} label)
        ($ ui/segmented-control
          {:value effective-value
           :onChange on-change
           :data (clj->js options)
           :disabled (or disabled? loading? waiting-for-dependent?)})
        (when error
          ($ ui/text {:size "xs" :c "red"} error)))

      :autocomplete
      ($ ui/autocomplete
        (cond-> {:ref ac-input-ref
                 :label label
                 :value ac-display
                 :data (clj->js (mapv :label options))
                 :onChange (fn [val]
                             (set-ac-text! val)
                             (if @ac-just-selected
                               ;; Selection just happened — update display only, skip search
                               (reset! ac-just-selected false)
                               (do
                                 (when on-search (on-search val))
                                 ;; Clear selected value when user edits text
                                 (when value (on-change nil)))))
                 :onOptionSubmit (fn [val]
                                   (reset! ac-just-selected true)
                                   (set-ac-text! val)
                                   (when-let [opt (first (filter #(= (:label %) val) options))]
                                     (on-change (:value opt))))
                 :placeholder placeholder
                 ;; Every other branch renders :description; autocomplete
                 ;; silently dropped it, so a field explaining what picking
                 ;; something here COSTS had nowhere to say it (#803).
                 :description (:description ui)
                 :required required
                 :disabled (or disabled? waiting-for-dependent?)
                 :error error}
          on-create-new
          (assoc :inputContainer
                 (extras-under-input
                  ($ ui/button {:variant "subtle" :size "xs"
                                :onClick on-create-new :mt "xs"}
                    (str "+ Add " (or create-label "New") "..."))))))

      :multi-select
      ($ ui/multi-select
        {:label label
         :value (clj->js (or effective-value []))
         :data (clj->js options)
         :onChange #(on-change (js->clj %))
         :placeholder placeholder
         :searchable true
         :clearable true
         :required required
         :disabled (or disabled? loading? waiting-for-dependent?)
         :error error})

      ;; Default: Select dropdown
      (let [has-descriptions? (some :description options)
            render-option (when has-descriptions?
                            (fn [^js item]
                              (let [option (.-option item)]
                                ($ ui/stack {:gap 0}
                                  ($ ui/text {:size "sm"} (.-label option))
                                  (when-let [desc (.-description option)]
                                    ($ ui/text {:size "xs" :c "dimmed"} desc))))))
            other-active? (or (= value show-text-on) has-custom-value?)
            show-other-input? (boolean (and show-text-on other-active?))
            handle-select-change (fn [v]
                                  (cond
                                    ;; Selecting the "other" trigger — clear text, set trigger value
                                    (and show-text-on (= v show-text-on))
                                    (do (set-other-text! "")
                                        (on-change v))

                                    ;; Normal option — clear other text
                                    show-text-on
                                    (do (set-other-text! "")
                                        (on-change v))

                                    :else (on-change v)))]
        ($ ui/select
          (cond-> {:label label
                   :value effective-value
                   :data (clj->js options)
                   :onChange handle-select-change
                   :placeholder placeholder
                   :searchable true
                   :clearable true
                   :required required
                   :disabled (or disabled? loading? waiting-for-dependent?)
                   :error error}
            render-option (assoc :renderOption render-option)

            (or on-create-new show-other-input?)
            (assoc :inputContainer
                   (extras-under-input
                    ($ :<>
                      (when on-create-new
                        ($ ui/button {:variant "subtle" :size "xs"
                                      :onClick on-create-new :mt "xs"}
                          (str "+ Add " (or create-label "New") "...")))
                      (when show-other-input?
                        ($ ui/text-input
                          {:placeholder "Please specify..."
                           :value other-text
                           :onChange (fn [e]
                                       (let [v (.. e -target -value)]
                                         (set-other-text! v)
                                         (if (seq v)
                                           (on-change v)
                                           (on-change show-text-on))))
                           :mt "xs"
                           :disabled disabled?})))))))))))
